import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Users, BadgeCheck, GraduationCap, Eye, MapPin, Mail, Phone, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";
import { api, formatApiError } from "@/lib/api";

export default function AgencyPublicProfile() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [agencyRes, listingsRes] = await Promise.all([
                    api.get(`/agencies/${slug}`),
                    api.get(`/agencies/${slug}/listings?limit=12`),
                ]);
                if (cancelled) return;
                setData(agencyRes.data);
                setListings(listingsRes.data.items || []);
            } catch (err) {
                if (cancelled) return;
                const msg = formatApiError(err.response?.data?.detail) || "Agency not found";
                setError(msg);
                toast.error(msg);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-[60vh] grid place-items-center">
                <Loader2 className="h-8 w-8 animate-spin text-coral" />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div data-testid="agency-not-found" className="min-h-[50vh] grid place-items-center text-center px-4">
                <div>
                    <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <h1 className="font-display text-2xl font-bold text-foreground mb-2">Agency not found</h1>
                    <p className="font-app text-sm text-muted-foreground mb-5">{error}</p>
                    <Button asChild className="bg-coral hover:bg-coral-deep text-white">
                        <Link to="/marketplace">Browse marketplace</Link>
                    </Button>
                </div>
            </div>
        );
    }

    const { agency, stats, members } = data;
    const owner = members?.find((m) => m.id === agency.owner_user_id);

    return (
        <div data-testid="agency-public-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="bg-ocean-deep-grad text-white py-12 lg:py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-start gap-4 flex-wrap">
                        {agency.logo_url ? (
                            <img src={agency.logo_url} alt={agency.name} className="h-16 w-16 rounded-lg object-cover bg-white/10 border border-white/15" />
                        ) : (
                            <div className="h-16 w-16 rounded-lg bg-coral/20 border border-coral/40 grid place-items-center">
                                <Building2 className="h-8 w-8 text-coral" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-coral/15 border border-coral/30 mb-2">
                                <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                                    Verified Agency
                                </span>
                            </div>
                            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-1">
                                {agency.name}
                            </h1>
                            <div className="flex flex-wrap items-center gap-3 text-sm font-app text-white/85 mt-2">
                                {agency.office_region && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="h-3.5 w-3.5" />
                                        {agency.office_region}{agency.office_island ? `, ${agency.office_island}` : ""}
                                    </span>
                                )}
                                {agency.contact_email && (
                                    <a className="inline-flex items-center gap-1.5 hover:text-turquoise" href={`mailto:${agency.contact_email}`}>
                                        <Mail className="h-3.5 w-3.5" /> {agency.contact_email}
                                    </a>
                                )}
                                {agency.contact_phone && (
                                    <a className="inline-flex items-center gap-1.5 hover:text-turquoise" href={`tel:${agency.contact_phone}`}>
                                        <Phone className="h-3.5 w-3.5" /> {agency.contact_phone}
                                    </a>
                                )}
                            </div>
                            {agency.description && (
                                <p className="font-app text-sm text-white/80 mt-3 max-w-3xl leading-relaxed">{agency.description}</p>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-8 border-b border-border bg-card">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="agency-stats-strip">
                    <Stat label="Team size" value={stats?.member_count || 0} icon={Users} />
                    <Stat label="Active listings" value={stats?.listings_active || 0} icon={Building2} accent />
                    <Stat label="Verified agents" value={stats?.verified_count || 0} icon={BadgeCheck} />
                    <Stat label="Certified agents" value={stats?.certified_count || 0} icon={GraduationCap} accent />
                    <Stat label="Total views" value={(stats?.total_views || 0).toLocaleString()} icon={Eye} />
                </div>
            </section>

            {/* LISTINGS GRID */}
            {listings.length > 0 && (
                <section className="py-10 lg:py-14">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground mb-1">
                            Featured listings by {agency.name}
                        </h2>
                        <p className="font-app text-sm text-muted-foreground mb-6">
                            Showing the latest {listings.length} active listings from the team.
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agency-listings-grid">
                            {listings.map((l) => (
                                <PropertyCard key={l.id} listing={l} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* TEAM ROSTER */}
            <section className="py-10 lg:py-14 bg-secondary/30 dark:bg-card/40">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground mb-6">The team</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agency-public-roster">
                        {(members || []).map((m) => (
                            <Card key={m.id} className="p-4 bg-card flex items-center gap-3" data-testid={`agency-public-member-${m.id}`}>
                                <div className="h-10 w-10 rounded-full bg-coral/15 grid place-items-center shrink-0">
                                    <span className="font-display text-sm font-bold text-coral">
                                        {(m.full_name || "?").trim().slice(0, 1).toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-display text-sm font-bold text-foreground truncate">{m.full_name || "Agent"}</span>
                                        {m.id === agency.owner_user_id && (
                                            <Badge className="bg-coral text-white text-[10px]">Owner</Badge>
                                        )}
                                        {m.agent_verified && (
                                            <BadgeCheck className="h-3.5 w-3.5 text-fiji-green" aria-label="Verified" />
                                        )}
                                        {m.agent_trained && (
                                            <GraduationCap className="h-3.5 w-3.5 text-coral" aria-label="Certified" />
                                        )}
                                    </div>
                                    {m.agency_role && m.id !== agency.owner_user_id && (
                                        <div className="font-app text-[11px] text-muted-foreground capitalize">
                                            {m.agency_role.replace("_", " ")}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        ))}
                    </div>
                    {owner && (
                        <p className="font-app text-xs text-muted-foreground mt-4 text-center">
                            Founded by {owner.full_name}. Member since {new Date(agency.created_at).toLocaleDateString()}.
                        </p>
                    )}
                </div>
            </section>
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return (
        <Card className={`p-4 ${accent ? "bg-coral/5 dark:bg-coral/10 border-coral/30" : "bg-card"}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`h-3.5 w-3.5 ${accent ? "text-coral" : "text-muted-foreground"}`} />
                <span className="font-app text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums ${accent ? "text-coral" : "text-foreground"}`}>
                {value}
            </div>
        </Card>
    );
}
